/* eslint-disable */
/**
 * Phase 98m — Panneau latéral détail d'un contact.
 *
 * Fonctionnalités :
 * - Vue synthétique (nom, rôles, email, tél, adresse)
 * - Édition inline des champs modifiables (via PUT /contacts/entry sur la 1ère source éditable)
 * - Notes libres (CRUD /contacts/notes)
 * - Historique HACCP livraisons + lots pour les REVENDEURS (via /resellers/{id}/haccp-history)
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
  Mail, Phone, MapPin, Pencil, Save, X, StickyNote, Package,
  History, Trash2, Building2, Briefcase, Users, ChefHat, Sparkles, ShieldAlert,
  FileText, FileSpreadsheet, Calendar, Send, CheckCircle2, Clock, MailQuestion,
} from "lucide-react";

const ROLE_META = {
  AUTOMATE: { label: "Automate", icon: Building2, color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  REVENDEUR: { label: "Revendeur B2B", icon: Briefcase, color: "bg-amber-100 text-amber-900 border-amber-300" },
  B2C: { label: "Client B2C", icon: Users, color: "bg-blue-100 text-blue-800 border-blue-300" },
  TRAITEUR: { label: "Traiteur", icon: ChefHat, color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
  PROSPECT: { label: "Prospect", icon: Sparkles, color: "bg-slate-100 text-slate-700 border-slate-300" },
};

// Phase 101fw (Feb 2026) — Fix bug « impossible d'éditer un contact ».
// Le backend supporte 4 modules éditables ; le frontend n'en avait 3 (oubli
// de `standalone_contact` créé Phase 101du.6). Ajout du 4e + libellés FR
// pour améliorer le message readonly des types non-éditables.
const EDITABLE_MODULES = new Set(["distributor", "reseller", "shop_customer", "standalone_contact"]);
const READONLY_MODULE_LABELS = {
  catering_quote: "devis traiteur",
  reseller_application: "candidature revendeur",
  company_letter: "courrier officiel",
};

// Phase 101ei — Format CHF standard suisse (2 décimales, séparateur d'apostrophe)
const chf = (v) => {
  const n = Number(v || 0);
  return `CHF ${n.toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function ContactDetailPanel({ contact, open, onClose, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [haccp, setHaccp] = useState(null);
  const [loadingHaccp, setLoadingHaccp] = useState(false);
  const [saving, setSaving] = useState(false);
  // Phase 101fp — Envoi lien d'activation revendeur depuis la fiche contact
  const [sendingActivation, setSendingActivation] = useState(false);

  // 1re source éditable trouvée (ordre reseller > distributor > shop_customer)
  const primarySource = useMemo(() => {
    if (!contact?.sources) return null;
    const preferred = ["reseller", "distributor", "shop_customer"];
    for (const p of preferred) {
      const s = contact.sources.find((x) => x.module === p);
      if (s) return s;
    }
    return contact.sources[0] || null;
  }, [contact]);

  const isReseller = Boolean(contact?.roles?.includes("REVENDEUR"));
  const resellerSource = contact?.sources?.find((s) => s.module === "reseller");

  useEffect(() => {
    if (!contact) return;
    setEditing(false);
    setForm({
      name: contact.name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      address: contact.address || "",
      postal_code: contact.postal_code || "",
      city: contact.city || "",
      notes: contact.notes || "",
    });
    // Charge notes
    (async () => {
      if (!primarySource) return;
      try {
        const { data } = await api.get(`/contacts/notes?source_module=${primarySource.module}&source_id=${primarySource.id}`);
        setNotes(data.notes || []);
      } catch (e) {
        setNotes([]);
      }
    })();
    // Charge HACCP si revendeur
    if (isReseller && resellerSource) {
      setHaccpPeriod("all");  // Reset filtre à chaque ouverture
      loadHaccp(resellerSource.id, "all");
      loadCommercial(resellerSource.id, "all");
    } else {
      setHaccp(null);
      setCommercial(null);
    }
  }, [contact, primarySource, isReseller, resellerSource]);

  // Phase 101dz — Filtre période
  const [haccpPeriod, setHaccpPeriod] = useState("all");

  const periodToDates = (period) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    const y = now.getFullYear();
    const m = now.getMonth();
    if (period === "month") return { date_from: iso(new Date(y, m, 1)), date_to: iso(now) };
    if (period === "3months") return { date_from: iso(new Date(y, m - 3, 1)), date_to: iso(now) };
    if (period === "12months") return { date_from: iso(new Date(y - 1, m + 1, 1)), date_to: iso(now) };
    return { date_from: null, date_to: null };
  };

  const loadHaccp = async (rid, period) => {
    setLoadingHaccp(true);
    try {
      const { date_from, date_to } = periodToDates(period);
      const params = {};
      if (date_from) params.date_from = date_from;
      if (date_to) params.date_to = date_to;
      const { data } = await api.get(`/resellers/${rid}/haccp-history`, { params });
      setHaccp(data);
    } catch (e) {
      setHaccp(null);
    } finally {
      setLoadingHaccp(false);
    }
  };

  const changePeriod = (newPeriod) => {
    setHaccpPeriod(newPeriod);
    if (resellerSource) {
      loadHaccp(resellerSource.id, newPeriod);
      loadCommercial(resellerSource.id, newPeriod);
    }
  };

  // Phase 101ei — Historique commercial (commandes + factures + CA)
  const [commercial, setCommercial] = useState(null);
  const [loadingCommercial, setLoadingCommercial] = useState(false);
  const loadCommercial = async (rid, period) => {
    setLoadingCommercial(true);
    try {
      const { date_from, date_to } = periodToDates(period);
      const params = {};
      if (date_from) params.date_from = date_from;
      if (date_to) params.date_to = date_to;
      const { data } = await api.get(`/resellers/${rid}/commercial-history`, { params });
      setCommercial(data);
    } catch (e) {
      setCommercial(null);
    } finally {
      setLoadingCommercial(false);
    }
  };

  const exportHaccp = async (format) => {
    if (!resellerSource) return;
    try {
      const { date_from, date_to } = periodToDates(haccpPeriod);
      const params = new URLSearchParams();
      if (date_from) params.set("date_from", date_from);
      if (date_to) params.set("date_to", date_to);
      const qs = params.toString();
      const url = `/resellers/${resellerSource.id}/haccp-history.${format}${qs ? "?" + qs : ""}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: format === "csv" ? "text/csv;charset=utf-8" : "application/pdf",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `HACCP_${(contact?.name || "revendeur").replace(/\s+/g, "_")}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Erreur export ${format.toUpperCase()}`);
    }
  };

  const canEdit = primarySource && EDITABLE_MODULES.has(primarySource.module);

  const saveEdits = async () => {
    if (!primarySource) return;
    setSaving(true);
    try {
      const payload = {
        source_module: primarySource.module,
        source_id: primarySource.id,
      };
      // Mapping champ "Nom du contact" (form.name) → collection :
      //   - reseller & distributor : `contact_name` (le nom légal reste sur `name`)
      //   - shop_customer : `name` (unique champ nominal)
      //   - standalone_contact : `name` (Phase 101fw — était bloqué avant fix)
      if (primarySource.module === "shop_customer" || primarySource.module === "standalone_contact") {
        payload.name = form.name;
      } else {
        payload.contact_name = form.name;
      }
      payload.email = form.email;
      payload.phone = form.phone;
      payload.address = form.address;
      payload.postal_code = form.postal_code;
      payload.city = form.city;
      payload.notes = form.notes;
      await api.put("/contacts/entry", payload);
      toast.success("Contact mis à jour");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    const txt = newNote.trim();
    if (!txt || !primarySource) return;
    try {
      const { data } = await api.post("/contacts/notes", {
        source_module: primarySource.module,
        source_id: primarySource.id,
        note: txt,
      });
      setNotes([data, ...notes]);
      setNewNote("");
      toast.success("Note ajoutée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur ajout note");
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/contacts/notes/${noteId}`);
      setNotes(notes.filter((n) => n.id !== noteId));
      toast.success("Note supprimée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  };

  // Phase 101fp — Envoi/relance du lien d'activation revendeur
  const sendActivationLink = async () => {
    if (!resellerSource || !contact.reseller_id) return;
    if (!contact.email) {
      toast.error("Renseignez d'abord un email sur ce revendeur.");
      return;
    }
    const isRelance = contact.reseller_activation_state === "invited";
    const confirmMsg = isRelance
      ? `Renvoyer le lien d'activation à ${contact.email} ? L'ancien lien sera invalidé.`
      : `Envoyer un lien d'activation à ${contact.email} ? Valable 7 jours.`;
    if (!window.confirm(confirmMsg)) return;
    setSendingActivation(true);
    try {
      const { data } = await api.post(`/admin/resellers/${contact.reseller_id}/send-activation-link`);
      if (data.email_sent) {
        toast.success(`Email envoyé à ${contact.email}`);
      } else if (data.email_skipped) {
        toast.warning(`Token généré, email non envoyé (${data.email_error || "Resend non configuré"}). Communique le lien manuellement.`);
      } else {
        toast.warning("Token généré, l'email a peut-être échoué.");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi du lien");
    } finally {
      setSendingActivation(false);
    }
  };

  if (!contact) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="contact-detail-panel">
        <SheetHeader className="mb-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <SheetTitle className="text-xl">{contact.name || "Contact sans nom"}</SheetTitle>
            <div className="flex flex-wrap gap-1">
              {(contact.roles || []).map((r) => {
                const meta = ROLE_META[r];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <span key={r} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border ${meta.color}`}>
                    <Icon className="w-3 h-3" />
                    {meta.label}
                  </span>
                );
              })}
            </div>
          </div>
          {contact.organization && (
            <div className="text-sm text-[#6B6E6B]">{contact.organization}</div>
          )}
        </SheetHeader>

        {/* Coordonnées + édition */}
        <section className="border border-[#E5E5E0] rounded-md p-4 mb-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#1A1C1A] flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#1F4E3D]" /> Coordonnées
            </h3>
            {canEdit && !editing && (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="contact-edit-btn">
                <Pencil className="w-3 h-3 mr-1" /> Modifier
              </Button>
            )}
            {editing && (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="contact-cancel-btn">
                  <X className="w-3 h-3 mr-1" /> Annuler
                </Button>
                <Button size="sm" onClick={saveEdits} disabled={saving} className="bg-[#1F4E3D]" data-testid="contact-save-btn">
                  <Save className="w-3 h-3 mr-1" /> {saving ? "Enreg…" : "Enregistrer"}
                </Button>
              </div>
            )}
          </div>

          {!editing ? (
            <div className="space-y-2 text-sm">
              {contact.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-[#6B6E6B]" /> <a href={`mailto:${contact.email}`} className="text-[#1F4E3D] hover:underline">{contact.email}</a></div>}
              {contact.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-[#6B6E6B]" /> <a href={`tel:${contact.phone}`} className="hover:underline">{contact.phone}</a></div>}
              {(contact.address || contact.city) && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-3 h-3 text-[#6B6E6B] mt-0.5" />
                  <div>
                    {contact.address && <div>{contact.address}</div>}
                    {(contact.postal_code || contact.city) && <div>{[contact.postal_code, contact.city].filter(Boolean).join(" ")}</div>}
                  </div>
                </div>
              )}
              {contact.notes && (
                <div className="mt-2 p-2 bg-[#FAFAF7] border-l-2 border-[#C1A063] text-xs italic text-[#575A57]">
                  {contact.notes}
                </div>
              )}
              {!canEdit && (
                <div className="text-xs text-[#6B6E6B] mt-3 italic" data-testid="contact-readonly-hint">
                  {(() => {
                    const label = READONLY_MODULE_LABELS[primarySource?.module];
                    if (label) {
                      return `Cette adresse vient d'un ${label}. Pour l'éditer sans toucher au document d'origine, crée un contact autonome (bouton « Nouveau contact » du carnet).`;
                    }
                    return `Édition non disponible pour ce type de contact (${primarySource?.module || "inconnu"}).`;
                  })()}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Nom du contact</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Email</label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-email" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Téléphone</label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Adresse</label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="edit-address" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Code postal</label>
                <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="edit-postal" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Ville</label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="edit-city" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Notes (fiche officielle)</label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="edit-notes" />
              </div>
            </div>
          )}
        </section>

        {/* Phase 101fp — Activation compte revendeur (bouton envoi/relance lien magique) */}
        {isReseller && (
          <section
            className="border border-[#E5E5E0] rounded-md p-4 mb-4 bg-white"
            data-testid="reseller-activation-section"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                {contact.reseller_activation_state === "activated" ? (
                  <CheckCircle2 className="w-4 h-4 text-[#1F4E3D] mt-0.5 shrink-0" />
                ) : contact.reseller_activation_state === "invited" ? (
                  <Clock className="w-4 h-4 text-[#8C6B2F] mt-0.5 shrink-0" />
                ) : (
                  <MailQuestion className="w-4 h-4 text-[#A3A6A3] mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-[#1A1C1A]">Activation compte boutique</h3>
                  {contact.reseller_activation_state === "activated" ? (
                    <p className="text-xs text-[#575A57] mt-0.5" data-testid="reseller-activation-state">
                      <span className="font-medium text-[#1F4E3D]">Compte activé.</span> Le revendeur peut se connecter à la boutique et voir ses tarifs négociés.
                    </p>
                  ) : contact.reseller_activation_state === "invited" ? (
                    <p className="text-xs text-[#575A57] mt-0.5" data-testid="reseller-activation-state">
                      <span className="font-medium text-[#8C6B2F]">Lien envoyé, en attente d'activation.</span>
                      {contact.reseller_activation_sent_at && (
                        <span className="text-[10px] text-[#A3A6A3] block mt-0.5">
                          Envoyé le {new Date(contact.reseller_activation_sent_at).toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" })} · valable 7 jours
                        </span>
                      )}
                    </p>
                  ) : (
                    <p className="text-xs text-[#575A57] mt-0.5" data-testid="reseller-activation-state">
                      <span className="font-medium text-[#575A57]">Jamais invité.</span> Envoie un lien pour que le revendeur puisse activer son compte boutique.
                    </p>
                  )}
                </div>
              </div>
              {contact.reseller_activation_state !== "activated" && (
                <Button
                  size="sm"
                  onClick={sendActivationLink}
                  disabled={sendingActivation || !contact.email}
                  className={contact.reseller_activation_state === "invited"
                    ? "bg-[#8C6B2F] hover:bg-[#755925] text-white"
                    : "bg-[#1F4E3D] hover:bg-[#173B2E] text-white"}
                  data-testid="reseller-send-activation-btn"
                  title={!contact.email ? "Renseigne d'abord un email sur ce revendeur" : ""}
                >
                  <Send className="w-3 h-3 mr-1" />
                  {sendingActivation
                    ? "Envoi…"
                    : contact.reseller_activation_state === "invited"
                    ? "Renvoyer le lien"
                    : "Envoyer le lien"}
                </Button>
              )}
            </div>
          </section>
        )}

        {/* Phase 101ei — Historique commercial (commandes + factures + CA) */}
        {isReseller && (
          <section className="border border-[#E5E5E0] rounded-md p-4 mb-4 bg-white" data-testid="commercial-section">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-[#1A1C1A] flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-[#1F4E3D]" /> Historique commercial
              </h3>
              {commercial?.kpis && (
                <div className="flex gap-2 text-xs flex-wrap">
                  <Badge variant="secondary" data-testid="cm-kpi-orders">{commercial.kpis.total_orders} commande(s)</Badge>
                  <Badge className="bg-[#1F4E3D] text-white">CA {chf(commercial.kpis.ca_ttc)}</Badge>
                  <Badge className={commercial.kpis.outstanding_amount > 0 ? "bg-[#C05C47] text-white" : "bg-[#5A8F76] text-white"}>
                    {commercial.kpis.outstanding_amount > 0 ? `À encaisser ${chf(commercial.kpis.outstanding_amount)}` : "Tout encaissé"}
                  </Badge>
                </div>
              )}
            </div>

            {loadingCommercial ? (
              <div className="text-xs text-[#575A57] italic py-2">Chargement…</div>
            ) : !commercial || (commercial.orders.length === 0 && commercial.invoices.length === 0) ? (
              <div className="text-xs text-[#575A57] italic py-2" data-testid="commercial-empty">
                Aucune commande ni facture sur la période sélectionnée.
              </div>
            ) : (
              <div className="space-y-4">
                {/* KPIs détaillés */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-2">
                    <div className="text-[9px] uppercase text-[#6B6E6B]">CA TTC</div>
                    <div className="text-base font-semibold text-[#1F4E3D]" data-testid="cm-ca-ttc">{chf(commercial.kpis.ca_ttc)}</div>
                  </div>
                  <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-2">
                    <div className="text-[9px] uppercase text-[#6B6E6B]">CA HT</div>
                    <div className="text-base font-semibold text-[#1A1C1A]">{chf(commercial.kpis.ca_ht)}</div>
                  </div>
                  <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-2">
                    <div className="text-[9px] uppercase text-[#6B6E6B]">Payé</div>
                    <div className="text-base font-semibold text-[#5A8F76]">{chf(commercial.kpis.paid_amount)}</div>
                  </div>
                  <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-2">
                    <div className="text-[9px] uppercase text-[#6B6E6B]">Panier moyen</div>
                    <div className="text-base font-semibold text-[#1A1C1A]">{chf(commercial.kpis.avg_order_ttc)}</div>
                  </div>
                </div>

                {/* Commandes */}
                {commercial.orders.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#575A57] mb-1.5">
                      Commandes ({commercial.orders.length})
                    </div>
                    <div className="border border-[#E5E5E0] rounded overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-[#F9F9F6] text-[9px] uppercase text-[#6B6E6B]">
                          <tr>
                            <th className="text-left px-2 py-1.5">Date</th>
                            <th className="text-left px-2 py-1.5">Réf.</th>
                            <th className="text-left px-2 py-1.5">Statut</th>
                            <th className="text-right px-2 py-1.5">Montant HT</th>
                            <th className="w-6"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {commercial.orders.slice(0, 20).map((o) => (
                            <tr key={o.id} className="border-t border-[#F0F0EB]" data-testid={`cm-order-${o.reference}`}>
                              <td className="px-2 py-1 font-mono">{(o.order_date || "").slice(0, 10)}</td>
                              <td className="px-2 py-1 font-mono">
                                <a href={`/commandes-clients/${o.id}`} className="text-[#1F4E3D] hover:underline">{o.reference}</a>
                              </td>
                              <td className="px-2 py-1">
                                <Badge variant="outline" className="text-[9px]">{o.status}</Badge>
                              </td>
                              <td className="px-2 py-1 text-right font-mono">{chf(o.total_ht_estimated)}</td>
                              <td className="px-2 py-1 text-right">
                                {o.invoice_id && <span className="text-[9px] text-[#5A8F76]" title="Facturée">✓</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {commercial.orders.length > 20 && (
                      <div className="text-[10px] text-[#6B6E6B] italic mt-1">
                        Affiche 20 dernières · {commercial.orders.length - 20} de plus non affichées
                      </div>
                    )}
                  </div>
                )}

                {/* Factures */}
                {commercial.invoices.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#575A57] mb-1.5">
                      Factures ({commercial.invoices.length})
                    </div>
                    <div className="border border-[#E5E5E0] rounded overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-[#F9F9F6] text-[9px] uppercase text-[#6B6E6B]">
                          <tr>
                            <th className="text-left px-2 py-1.5">Émission</th>
                            <th className="text-left px-2 py-1.5">Livraison</th>
                            <th className="text-left px-2 py-1.5">N°</th>
                            <th className="text-left px-2 py-1.5">Statut</th>
                            <th className="text-right px-2 py-1.5">TTC</th>
                            <th className="text-right px-2 py-1.5">Payé</th>
                            <th className="text-right px-2 py-1.5">Reste</th>
                          </tr>
                        </thead>
                        <tbody>
                          {commercial.invoices.slice(0, 20).map((i) => (
                            <tr key={i.id} className="border-t border-[#F0F0EB]" data-testid={`cm-invoice-${i.reference}`}>
                              <td className="px-2 py-1 font-mono">{(i.issue_date || "").slice(0, 10)}</td>
                              <td className="px-2 py-1 font-mono text-[#575A57]">{(i.delivery_date || "").slice(0, 10) || "—"}</td>
                              <td className="px-2 py-1 font-mono">
                                <a href={`/factures/${i.id}`} className="text-[#1F4E3D] hover:underline">{i.reference}</a>
                              </td>
                              <td className="px-2 py-1">
                                <Badge
                                  variant="outline"
                                  className={`text-[9px] ${
                                    i.status === "PAYEE" ? "bg-[#EAF4EE] text-[#1F4E3D] border-[#5A8F76]" :
                                    i.status === "ANNULEE" ? "bg-gray-100 text-gray-500" :
                                    i.status === "EN_RETARD" ? "bg-[#FBEDEA] text-[#C05C47] border-[#C05C47]" :
                                    "bg-[#FBF4E9] text-[#8C6B2F] border-[#8C6B2F]"
                                  }`}
                                >
                                  {i.status}
                                </Badge>
                              </td>
                              <td className="px-2 py-1 text-right font-mono">{chf(i.total_ttc)}</td>
                              <td className="px-2 py-1 text-right font-mono text-[#5A8F76]">{chf(i.paid_amount)}</td>
                              <td className={`px-2 py-1 text-right font-mono ${i.outstanding > 0 ? "text-[#C05C47] font-semibold" : "text-[#5A8F76]"}`}>
                                {chf(i.outstanding)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {commercial.invoices.length > 20 && (
                      <div className="text-[10px] text-[#6B6E6B] italic mt-1">
                        Affiche 20 dernières · {commercial.invoices.length - 20} de plus non affichées
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Historique HACCP (si revendeur) */}
        {isReseller && (
          <section className="border border-[#E5E5E0] rounded-md p-4 mb-4 bg-white" data-testid="haccp-section">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-[#1A1C1A] flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-[#C05C47]" /> Traçabilité HACCP
              </h3>
              {haccp && (
                <div className="flex gap-2 text-xs">
                  <Badge variant="secondary" data-testid="haccp-total-deliveries">{haccp.total_deliveries} livraisons</Badge>
                  <Badge variant="secondary" data-testid="haccp-total-barq">{Math.round(haccp.total_barquettes)} barquettes</Badge>
                  <Badge variant="secondary" data-testid="haccp-total-lots">{haccp.unique_lots} lots</Badge>
                </div>
              )}
            </div>

            {/* Phase 101dz — Filtre période + boutons export */}
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3 pb-3 border-b border-[#E5E5E0]">
              <div className="flex items-center gap-1 text-xs">
                <Calendar className="w-3.5 h-3.5 text-[#575A57]" />
                <span className="text-[#575A57] mr-1">Période :</span>
                {[
                  { k: "month", label: "Ce mois" },
                  { k: "3months", label: "3 mois" },
                  { k: "12months", label: "12 mois" },
                  { k: "all", label: "Tout" },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    onClick={() => changePeriod(opt.k)}
                    disabled={loadingHaccp}
                    data-testid={`haccp-period-${opt.k}`}
                    className={`px-2 py-1 rounded border text-[11px] transition-colors ${
                      haccpPeriod === opt.k
                        ? "bg-[#1F4E3D] text-white border-[#1F4E3D]"
                        : "bg-white text-[#575A57] border-[#E5E5E0] hover:border-[#1F4E3D]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-[#1F4E3D]/40 text-[#1F4E3D] hover:bg-[#EAF4EE]"
                  onClick={() => exportHaccp("csv")}
                  disabled={loadingHaccp || !haccp?.deliveries?.length}
                  data-testid="haccp-export-csv"
                  title="Exporter en CSV (Excel-compatible)"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> CSV
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-[#8C6B2F]/40 text-[#8C6B2F] hover:bg-[#FBF4E9]"
                  onClick={() => exportHaccp("pdf")}
                  disabled={loadingHaccp || !haccp?.deliveries?.length}
                  data-testid="haccp-export-pdf"
                  title="Exporter en PDF"
                >
                  <FileText className="w-3.5 h-3.5 mr-1" /> PDF
                </Button>
              </div>
            </div>

            {loadingHaccp ? (
              <p className="text-xs text-[#6B6E6B]">Chargement historique…</p>
            ) : !haccp?.deliveries?.length ? (
              <p className="text-xs text-[#6B6E6B] italic">Aucune livraison enregistrée pour ce revendeur.</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {haccp.deliveries.map((d) => (
                  <div key={d.delivery_id} className="border-l-2 border-[#1F4E3D] pl-3 py-1" data-testid={`haccp-delivery-${d.delivery_id}`}>
                    <div className="flex justify-between text-xs">
                      <span className="font-mono font-semibold">{d.reference || d.delivery_id.slice(0, 8)}</span>
                      <span className="text-[#6B6E6B]">{(d.delivery_date || "").slice(0, 10)}</span>
                    </div>
                    <div className="text-xs text-[#575A57] mb-1">
                      {d.status} · {d.total_quantity} barq · {d.lines_count} lignes
                    </div>
                    <div className="space-y-0.5">
                      {d.lines.map((ln, i) => (
                        <div key={i} className="text-[11px] flex justify-between gap-2 font-mono">
                          <span className="truncate">
                            <Package className="inline w-3 h-3 mr-1" />
                            {ln.product_name || ln.product_sku} · lot {ln.lot_number || "?"} · DLC {(ln.dlc || "?").slice(0,10)}
                          </span>
                          <span className="text-[#1F4E3D] font-semibold">{ln.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Notes libres */}
        {primarySource && (
          <section className="border border-[#E5E5E0] rounded-md p-4 mb-4 bg-white">
            <h3 className="text-sm font-semibold text-[#1A1C1A] flex items-center gap-2 mb-3">
              <StickyNote className="w-4 h-4 text-[#C1A063]" /> Notes internes ({notes.length})
            </h3>
            <div className="flex gap-2 mb-3">
              <Input
                placeholder="Nouvelle note (ex: pref facturation, contact secondaire, incident)…"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addNote()}
                data-testid="new-note-input"
              />
              <Button size="sm" onClick={addNote} disabled={!newNote.trim()} className="bg-[#1F4E3D]" data-testid="add-note-btn">
                Ajouter
              </Button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {notes.length === 0 ? (
                <p className="text-xs text-[#6B6E6B] italic">Aucune note. Ajoutez des infos internes libres.</p>
              ) : notes.map((n) => (
                <div key={n.id} className="p-2 bg-[#FAFAF7] border-l-2 border-[#C1A063] flex justify-between gap-2 text-sm" data-testid={`note-${n.id}`}>
                  <div className="flex-1">
                    <div>{n.note}</div>
                    <div className="text-[10px] text-[#6B6E6B] mt-1">
                      {(n.created_at || "").slice(0, 16).replace("T", " ")} · {n.created_by}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => deleteNote(n.id)} className="h-7 w-7 p-0 text-[#C05C47]" data-testid={`delete-note-${n.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}
      </SheetContent>
    </Sheet>
  );
}
